/* eslint-disable react-hooks/exhaustive-deps */
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { Box, Checkbox, Flex, HStack, Portal, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

export default function SelectAllTypeMultiSelect({
  value = "", // CSV string
  onChange,
  placeholder = "Select...",
  disabled,
  options,
  load,
  loading,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const normalizedValue = value ? value.split(",").map((v) => v.trim()) : [];

  const [fallbackOptions, setFallbackOptions] = useState([]);

  useEffect(() => {
    if (!normalizedValue.length) return;

    const missing = normalizedValue
      .filter((v) => !options.some((o) => o.value === v))
      .map((v) => ({ value: v, label: v }));

    setFallbackOptions(missing);
  }, [normalizedValue, options]);

  const mergedOptions = [...options, ...fallbackOptions];

  const handleToggle = () => {
    if (!isExpanded) {
      load();
    }
    setIsExpanded(!isExpanded);
  };

  // Close when clicking outside
  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isExpanded]);

  // Get selected options for collapsed view
  const selectedOptions = mergedOptions.filter((opt) =>
    normalizedValue.includes(opt.value)
  );

  const displayText =
    selectedOptions.length > 0
      ? selectedOptions.map((o) => o.label).join(", ")
      : placeholder;

  // Check if all are selected
  const allSelected =
    mergedOptions.length > 0 &&
    mergedOptions.every((opt) => normalizedValue.includes(opt.value));

  // Toggle individual option
  const toggleOption = (optValue) => {
    const exists = normalizedValue.includes(optValue);
    const updated = exists
      ? normalizedValue.filter((v) => v !== optValue)
      : [...normalizedValue, optValue];

    onChange(updated.join(","));
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (allSelected) {
      onChange("");
    } else {
      onChange(mergedOptions.map((opt) => opt.value).join(","));
    }
  };

  return (
    <Box position="relative">
      {/* Collapsed view - shows selected values */}
      <Flex
        ref={triggerRef}
        borderWidth="1px"
        borderRadius="md"
        p={3}
        cursor={disabled ? "not-allowed" : "pointer"}
        opacity={disabled ? 0.6 : 1}
        onClick={disabled ? undefined : handleToggle}
        justifyContent="space-between"
        alignItems="center"
        bg={isExpanded ? "gray.50" : "white"}
        _hover={disabled ? {} : { bg: "gray.50" }}
      >
        <Text
          fontSize="sm"
          color={selectedOptions.length > 0 ? "inherit" : "gray.500"}
          noOfLines={1}
          flex={1}
        >
          {displayText}
        </Text>
        {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Flex>

      {/* Expanded view - large centered popup */}
      {isExpanded && (
        <Portal>
          {/* Backdrop overlay */}
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.300"
            zIndex={9998}
            onClick={() => setIsExpanded(false)}
          />

          {/* Centered popup */}
          <Box
            ref={dropdownRef}
            position="fixed"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            width="600px"
            maxWidth="90vw"
            maxH="70vh"
            display="flex"
            flexDirection="column"
            borderWidth="1px"
            borderRadius="lg"
            bg="white"
            boxShadow="2xl"
            zIndex={9999}
          >
            {/* Header */}
            <Box p={6} pb={2} borderBottomWidth="1px">
              <Text fontSize="lg" fontWeight="semibold">
                Select Options
              </Text>
            </Box>

            {/* Select All */}
            <Box
              p={4}
              borderBottomWidth="1px"
              bg="gray.50"
              cursor="pointer"
              onClick={toggleSelectAll}
              _hover={{ bg: "gray.100" }}
            >
              <HStack>
                <Checkbox
                  isChecked={allSelected}
                  isDisabled={disabled}
                  pointerEvents="none"
                />
                <Text fontWeight="semibold">
                  {allSelected ? "Unselect All" : "Select All"}
                </Text>
              </HStack>
            </Box>

            {/* Options List */}
            <Box flex={1} overflowY="auto" p={2}>
              {loading ? (
                <Box fontSize="sm" color="gray.500">
                  Loading...
                </Box>
              ) : (
                <HStack spacing={3} align="stretch">
                  {mergedOptions.map((opt) => (
                    <Box
                      key={opt.value}
                      cursor="pointer"
                      onClick={() => !disabled && toggleOption(opt.value)}
                      _hover={{ bg: "gray.50" }}
                      p={2}
                      borderRadius="md"
                    >
                      <HStack>
                        <Checkbox
                          isChecked={normalizedValue.includes(opt.value)}
                          isDisabled={disabled}
                          pointerEvents="none"
                        />
                        <Text>{opt.label}</Text>
                      </HStack>
                    </Box>
                  ))}
                </HStack>
              )}
            </Box>
          </Box>
        </Portal>
      )}
    </Box>
  );
}
